import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import NewsletterForm from '@/components/feature/NewsletterForm';
import SEOSchema from '@/components/feature/SEOSchema';
import { aboutInfo } from '@/mocks/about';

export default function AboutPage() {
  return (
    <>
      <ScrollToTop />
      <SEOSchema type="Organization" data={{
        name: '久久精品',
        url: 'https://jiujiuboutique.com',
        description: aboutInfo.mission,
        foundingDate: '2018',
        email: aboutInfo.contact.email,
        telephone: aboutInfo.contact.phone,
        address: {
          '@type': 'PostalAddress',
          streetAddress: '南京西路1788号久久精品大厦15层',
          addressLocality: '上海',
          addressRegion: '上海',
          addressCountry: 'CN',
        },
      }} />
      <SEOSchema type="WebPage" data={{
        name: '关于我们 | 久久精品',
        description: '久久精品品牌介绍：品质经久不衰的精选好物平台，以严苛品控与持久品质为核心。了解品牌故事、口碑评价及联系方式。',
        inLanguage: 'zh-CN',
        isPartOf: { '@type': 'WebSite', name: '久久精品', url: 'https://jiujiuboutique.com' },
      }} />
      <SEOSchema type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jiujiuboutique.com/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://jiujiuboutique.com/about' },
        ],
      }} />
      <SEOSchema type="FAQPage" data={{
        mainEntity: [
          {
            '@type': 'Question',
            name: '久久精品的选品标准是什么？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '久久精品执行四重筛选体系：源头选材、工艺审核、设计评估、体验测试，确保每一件商品都经得起时间考验。',
            },
          },
          {
            '@type': 'Question',
            name: '久久精品支持哪些配送方式？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '久久精品与顺丰速运、京东物流等优质物流伙伴合作，全国主要城市可实现次日达或隔日达。',
            },
          },
          {
            '@type': 'Question',
            name: '不喜欢的商品可以退换吗？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '久久精品支持7天无理由退换货服务。在商品保持原状且不影响二次销售的前提下，您可以申请退换。',
            },
          },
        ],
      }} />

      <Navbar />
      <main className="min-h-screen pt-20">
        <Breadcrumb items={[{ label: '关于我们' }]} />

        {/* Brand Intro */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                {aboutInfo.brandName}
              </h1>
              <p className="text-lg md:text-xl text-primary-600 font-heading font-medium mb-4">
                {aboutInfo.tagline}
              </p>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                {aboutInfo.mission}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {aboutInfo.values.map((value, idx) => (
                <div key={idx} className="text-center p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`${value.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-2">{value.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-8 text-center">
              品牌故事
            </h2>
            <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              {aboutInfo.story.split('\n\n').map((para, idx) => (
                <p key={idx}>{para}</p>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-10 text-center">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300/60 md:-translate-x-px"></div>
              <div className="space-y-8">
                {aboutInfo.milestones.map((milestone, idx) => (
                  <div key={idx} className={`relative flex items-start gap-6 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-accent-500 transform -translate-x-1/2 mt-1.5 z-10"></div>
                    <div className={`ml-10 md:ml-0 md:w-1/2 ${idx % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                      <span className="text-sm font-bold text-accent-600 mb-1 block">{milestone.year}</span>
                      <h3 className="text-base font-heading font-semibold text-foreground-950 mb-1">{milestone.title}</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">{milestone.description}</p>
                    </div>
                    {idx % 2 === 0 ? <div className="hidden md:block md:w-1/2"></div> : <div className="hidden md:block md:w-1/2"></div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-10 text-center">
              核心团队
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {aboutInfo.team.map((member, idx) => (
                <div key={idx} className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-secondary-100 flex items-center justify-center">
                    <i className="ri-user-line text-2xl text-secondary-600"></i>
                  </div>
                  <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-1">{member.name}</h3>
                  <p className="text-xs text-primary-600 font-medium mb-2">{member.role}</p>
                  <p className="text-xs text-foreground-500 leading-relaxed">{member.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-10 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {aboutInfo.testimonials.map((t, idx) => (
                <div key={idx} className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">{t.content}</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-foreground-900">{t.name}</span>
                      <span className="text-xs text-foreground-400 ml-2">{t.location}</span>
                    </div>
                    <span className="text-xs text-foreground-400">{t.productName}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-10 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h3>
                    <a href={`mailto:${aboutInfo.contact.email}`} className="text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer">
                      {aboutInfo.contact.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1">客服电话</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1">微信公众号</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.wechat}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1">公司地址</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1">工作时间</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.workingHours}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <NewsletterForm />
      </main>
      <Footer />
    </>
  );
}